// ** React Imports
import { createContext, useEffect, useState, ReactNode } from 'react'
import authConfig from 'src/configs/auth'

// ** Next Import
import { useRouter } from 'next/router'

// ** Axios
import axios from 'axios'

// ** Config

// ** Types
import { AuthValuesType, RegisterParams, LoginParams, ErrCallbackType, UserDataType } from './types'
import { status } from './common'
import { UserManagementService } from 'src/service'
import { jwtDecode } from 'jwt-decode'

// ** Defaults
const defaultProvider: AuthValuesType = {
  user: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  isInitialized: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setIsInitialized: () => Boolean,
  register: () => Promise.resolve()
}

const AuthContext = createContext(defaultProvider)

type Props = {
  children: ReactNode
}

const AuthProvider = ({ children }: Props) => {
  // ** States
  const [user, setUser] = useState<UserDataType | null>(defaultProvider.user)
  const [loading, setLoading] = useState<boolean>(defaultProvider.loading)
  const [isInitialized, setIsInitialized] = useState<boolean>(defaultProvider.isInitialized)

  // ** Hooks
  const router = useRouter()

  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      setIsInitialized(true)
      const userDetails = window.localStorage.getItem('userData')!
      const azure_refresh_token = window.localStorage.getItem(authConfig.azureRefreshToken)
      if (JSON.parse(userDetails)?.email) {
        setLoading(true)
        const userDetailsResponse = await UserManagementService?.getUserDetails(JSON.parse(userDetails)?.email)
        setUser({ ...userDetailsResponse })
        if (!azure_refresh_token) {
          window.localStorage.clear()
        }
        setLoading(false)
      } else {
        await setLoading(false)
        redirect()
      }
    }
    initAuth()
  }, [])

  const handleLogin = async (params: LoginParams, errorCallback?: ErrCallbackType) => {
    try {
      const redirectUri = process.env.NEXT_PUBLIC_USER_LOGIN_REDIRECT_URL || ''
      const response = await axios.get(
        `${process.env.NEXT_PUBLIC_BASE_API_URL}auth/azure/authorize?code=${params.code}&code_verifier=${
          params.verifier
        }&redirect_uri=${encodeURIComponent(redirectUri)}`
      )

      if (response?.data.statusCode !== status.successCode) {
        if (errorCallback) errorCallback(response.data)

        return
      }

      const { data } = response
      const idToken: any = data.data.id_token
      const azureRefreshToken: any = data.data.refresh_token
      const decoded: any = jwtDecode(idToken)

      if (!decoded?.email) {
        if (errorCallback) errorCallback({ message: 'Invalid token' })

        return
      }
      const config = {
        headers: { Authorization: `Bearer ${idToken}` }
      }
      const userDetailsResponse = await UserManagementService?.getUserDetails(decoded?.email)
      const userResponse = await axios.get(
        `${process.env.NEXT_PUBLIC_BASE_API_URL}auth/access-token?azureRefreshToken=${azureRefreshToken}`,
        config
      )
      if (userResponse?.data.statusCode === status.successCode) {
        window.localStorage.setItem(authConfig.storageTokenKeyName, userResponse.data.data.access_token)
        window.localStorage.setItem(authConfig.refreshToken, userResponse.data.data.refresh_token)
        window.localStorage.setItem(authConfig.azureRefreshToken, azureRefreshToken)

        const returnUrl = router.query.returnUrl
        await setUser(userDetailsResponse)
        await window.localStorage.setItem('userData', JSON.stringify(userDetailsResponse))
        const redirectURL = returnUrl && returnUrl !== '/' ? returnUrl : '/'
        router.push(redirectURL as string)
      }
    } catch (err: any) {
      if (errorCallback) errorCallback(err)
    }
  }
  const redirect = () => {
    const returnUrl = router.asPath
    router.push(returnUrl as string)
  }

  const handleLogout = () => {
    setUser(null)
    setIsInitialized(false)
    window.localStorage.removeItem('userData')
    window.localStorage.removeItem(authConfig.storageTokenKeyName)
    window.localStorage.removeItem(authConfig.refreshToken)
    window.localStorage.clear()
    window.location.href = '/operation/login'
    router.push('/login')
  }

  const handleRegister = (params: RegisterParams, errorCallback?: ErrCallbackType) => {
    axios
      .post(authConfig.registerEndpoint, params)
      .then(res => {
        if (res.data.error) {
          if (errorCallback) errorCallback(res.data.error)
        } else {
          handleLogin({ code: params.code, verifier: params.verifier })
        }
      })
      .catch((err: { [key: string]: string }) => (errorCallback ? errorCallback(err) : null))
  }

  const values = {
    user,
    loading,
    setUser,
    setLoading,
    isInitialized,
    setIsInitialized,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister
  }

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
